import type { ButtonHTMLAttributes } from 'react';
import { useTheme } from '../hooks/useTheme.js';

// Pulsante circolare singolo: sole quando theme=light, luna quando theme=dark.
// Il click toggla il tema con cross-fade + rotate (durate 200ms/300ms).

export interface ThemeToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'sm' | 'md';
}

export function ThemeToggle({ size = 'md', className, style, ...rest }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const dim = size === 'sm' ? 32 : 40;
  const iconSize = size === 'sm' ? 16 : 20;

  const transition = 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Disattiva tema scuro' : 'Attiva tema scuro'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={`relative inline-flex items-center justify-center rounded-full border p-0 leading-none appearance-none transition-colors ${className ?? ''}`}
      style={{
        width: dim,
        height: dim,
        background: 'var(--paper)',
        borderColor: 'var(--line)',
        color: 'var(--ink)',
        ...style,
      }}
      {...rest}
    >
      {/* sun glyph */}
      <span
        aria-hidden="true"
        className="absolute inset-0 inline-flex items-center justify-center"
        style={{
          transition,
          transform: isDark ? 'rotate(-90deg) scale(0)' : 'rotate(0deg) scale(1)',
          opacity: isDark ? 0 : 1,
        }}
      >
        <SunIcon size={iconSize} />
      </span>
      {/* moon glyph */}
      <span
        aria-hidden="true"
        className="absolute inset-0 inline-flex items-center justify-center"
        style={{
          transition,
          transform: isDark ? 'rotate(0deg) scale(1)' : 'rotate(90deg) scale(0)',
          opacity: isDark ? 1 : 0,
        }}
      >
        <MoonIcon size={iconSize} />
      </span>
    </button>
  );
}

// Heroicons solid sun, geometricamente centrato sul viewBox (12,12).
function SunIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.25a.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-1.5 0V3a.75.75 0 0 1 .75-.75zM7.5 12a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0zM18.894 6.166a.75.75 0 0 0-1.06-1.06l-1.591 1.59a.75.75 0 1 0 1.06 1.061l1.591-1.59zM21.75 12a.75.75 0 0 1-.75.75h-2.25a.75.75 0 0 1 0-1.5H21a.75.75 0 0 1 .75.75zM17.834 18.894a.75.75 0 0 0 1.06-1.06l-1.59-1.591a.75.75 0 1 0-1.061 1.06l1.59 1.591zM12 18a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-1.5 0v-2.25A.75.75 0 0 1 12 18zM7.758 17.303a.75.75 0 0 0-1.061-1.06l-1.591 1.59a.75.75 0 0 0 1.06 1.061l1.591-1.59zM6 12a.75.75 0 0 1-.75.75H3a.75.75 0 0 1 0-1.5h2.25A.75.75 0 0 1 6 12zM6.697 7.757a.75.75 0 0 0 1.06-1.06l-1.59-1.591a.75.75 0 0 0-1.061 1.06l1.59 1.591z"
      />
    </svg>
  );
}

// Heroicons solid moon. Translate compensa il bias percettivo basso-sx della falce.
function MoonIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <g transform="translate(1.5 -0.5)">
        <path d="M21.752 15.002A9.718 9.718 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998z" />
      </g>
    </svg>
  );
}
